import axios from "axios";

const API_BASE = process.env.REACT_APP_API_URL;

const campaignAPI = axios.create({
  baseURL: `${API_BASE}/api/campaigns`,
  headers: {
    "Content-Type": "application/json",
  },
});

// Get all campaigns
export const getCampaigns = async (authToken, filters = {}) => {
  return campaignAPI.get("/", {
    params: filters,
    headers: {
      Authorization: `Bearer ${authToken}`,
    },
  });
};

// Get single campaign by ID
export const getCampaignById = async (authToken, campaignId) => {
  return campaignAPI.get(`/${campaignId}`, {
    headers: {
      Authorization: `Bearer ${authToken}`,
    },
  });
};

// Create new campaign
export const createCampaign = async (authToken, campaignData) => {
  const formData = new FormData();
  
  // Add all fields to FormData
  Object.keys(campaignData).forEach((key) => {
    if (key === "csvFile" && campaignData[key]) {
      formData.append("csvFile", campaignData[key]);
    } else if (key !== "csvFile") {
      let value = campaignData[key];
      
      // Convert boolean to string for FormData
      if (typeof value === "boolean") {
        value = value.toString();
      }
      
      // Handle null/undefined
      if (value === null || value === undefined) {
        value = "";
      }
      
      // Handle objects
      if (typeof value === "object") {
        formData.append(key, JSON.stringify(value));
      } else {
        formData.append(key, value);
      }
    }
  });

  return campaignAPI.post("/", formData, {
    headers: {
      Authorization: `Bearer ${authToken}`,
      "Content-Type": "multipart/form-data",
    },
  });
};

// Update campaign
export const updateCampaign = async (authToken, campaignId, campaignData) => {
  return campaignAPI.put(`/${campaignId}`, campaignData, {
    headers: {
      Authorization: `Bearer ${authToken}`,
    },
  });
};

// Delete campaign
export const deleteCampaign = async (authToken, campaignId) => {
  return campaignAPI.delete(`/${campaignId}`, {
    headers: {
      Authorization: `Bearer ${authToken}`,
    },
  });
};

// Get campaign contacts
export const getCampaignContacts = async (authToken, campaignId, filters = {}) => {
  return campaignAPI.get(`/${campaignId}/contacts`, {
    params: filters,
    headers: {
      Authorization: `Bearer ${authToken}`,
    },
  });
};

// Send campaign manually
export const sendCampaign = async (authToken, campaignId) => {
  return campaignAPI.post(`/${campaignId}/send`, {}, {
    headers: {
      Authorization: `Bearer ${authToken}`,
    },
  });
};

// Get campaign analytics
export const getCampaignAnalytics = async (authToken, campaignId) => {
  return campaignAPI.get(`/${campaignId}/analytics`, {
    headers: {
      Authorization: `Bearer ${authToken}`,
    },
  });
};

// Download campaign message logs as CSV
export const downloadCampaignLogs = async (authToken, campaignId) => {
  return campaignAPI.get(`/${campaignId}/logs/download`, {
    headers: {
      Authorization: `Bearer ${authToken}`,
    },
    responseType: 'blob', // Important: handle CSV as blob
  });
};

// Abort campaign (instant or scheduled)
export const abortCampaign = async (authToken, campaignId) => {
  return campaignAPI.post(`/${campaignId}/abort`, {}, {
    headers: {
      Authorization: `Bearer ${authToken}`,
    },
  });
};

// Reschedule a cancelled/aborted campaign
export const rescheduleCampaign = async (authToken, campaignId, scheduledTimestamp, timezone) => {
  return campaignAPI.post(`/${campaignId}/reschedule`, {
    scheduledTimestamp,
    timezone,
  }, {
    headers: {
      Authorization: `Bearer ${authToken}`,
    },
  });
};

// Update scheduled campaign time
export const updateCampaignSchedule = async (authToken, campaignId, scheduledTimestamp, timezone) => {
  return campaignAPI.put(`/${campaignId}/schedule`, {
    scheduledTimestamp,
    timezone,
  }, {
    headers: {
      Authorization: `Bearer ${authToken}`,
    },
  });
};

// Change WhatsApp account for campaign
export const changeCampaignWhatsappAccount = async (authToken, campaignId, whatsappAccountId) => {
  return campaignAPI.put(`/${campaignId}/whatsapp-account`, {
    whatsapp_account_id: whatsappAccountId,
  }, {
    headers: {
      Authorization: `Bearer ${authToken}`,
    },
  });
};

// Validate campaign
export const validateCampaign = async (authToken, campaignId) => {
  return campaignAPI.get(`/${campaignId}/validate`, {
    headers: {
      Authorization: `Bearer ${authToken}`,
    },
  });
};

// Get campaign sending status
export const getCampaignSendingStatus = async (authToken, campaignId) => {
  return campaignAPI.get(`/${campaignId}/sending-status`, {
    headers: {
      Authorization: `Bearer ${authToken}`,
    },
  });
};

// Cancel scheduled campaign
export const cancelScheduledCampaign = async (authToken, campaignId) => {
  return campaignAPI.delete(`/${campaignId}/schedule`, {
    headers: {
      Authorization: `Bearer ${authToken}`,
    },
  });
};

// Get scheduled campaign status
export const getScheduledCampaignStatus = async (authToken, campaignId) => {
  return campaignAPI.get(`/${campaignId}/schedule`, {
    headers: {
      Authorization: `Bearer ${authToken}`,
    },
  });
};

// Send test template message to a single phone number
export const sendTestTemplate = async (authToken, testData) => {
  const API_BASE = process.env.REACT_APP_WABA_SERVER_URL || process.env.REACT_APP_API_URL;
  return axios.post(`${API_BASE}/api/broadcast/send-template`, testData, {
    headers: {
      Authorization: `Bearer ${authToken}`,
      "Content-Type": "application/json",
    },
  });
};

export default campaignAPI;

